/**
 * Errors as the API sends them, and the codes the client is allowed to branch on.
 *
 * The rule from the backend: the message is for the human and may be reworded any time; the
 * `code` extension is the contract. Nothing in this codebase switches on a message string.
 */

/** RFC 7807, with the extension members the API adds. */
export type ProblemDetails = {
  readonly type?: string;
  readonly title?: string;
  readonly status?: number;
  readonly detail?: string;
  /** The stable machine-readable code. Present on every error the API generates itself. */
  readonly code?: string;
  /** Field-level validation messages, keyed by property name. */
  readonly fields?: Readonly<Record<string, readonly string[]>>;
  /** `INVALID_PIN` carries this so the screen can warn before the wipe. */
  readonly attemptsRemaining?: number;
  /** `INSUFFICIENT_STOCK` carries these. */
  readonly variantId?: string;
  readonly available?: number;
  readonly requiredPermission?: string;
  /** `PRICE_BELOW_FLOOR_APPROVAL_REQUIRED` carries this — the request to poll and resubmit with. */
  readonly approvalRequestId?: string;
};

export const ErrorCode = {
  /** No device row could be matched at all. Wipe everything local and show the password screen. */
  DeviceRevoked: 'DEVICE_REVOKED',
  /** A password login on the same channel replaced this device. */
  SignedInElsewhere: 'SIGNED_IN_ELSEWHERE',
  /** An owner explicitly revoked this device. */
  DeviceRevokedByOwner: 'DEVICE_REVOKED_BY_OWNER',
  /** Five wrong PINs. */
  PinAttemptsExceeded: 'PIN_ATTEMPTS_EXCEEDED',
  /** The user changed their own password, which signs out every device including this one. */
  PasswordChanged: 'PASSWORD_CHANGED',
  /**
   * Two requests renewed the same device at once and this one lost the race at the database —
   * not a session end, just a retryable conflict.
   */
  RenewConflict: 'RENEW_CONFLICT',
  InvalidCredentials: 'INVALID_CREDENTIALS',
  AccountDeactivated: 'ACCOUNT_DEACTIVATED',
  /** The person's home shop was deactivated; they are signed out until it is reactivated. */
  ShopDeactivated: 'SHOP_DEACTIVATED',
  /** Too many wrong passwords in a row; refused until the lock runs out or the owner resets it. */
  AccountLocked: 'ACCOUNT_LOCKED',
  /** Too many wrong passwords typed while signed in: this device was signed out. */
  PasswordAttemptsExceeded: 'PASSWORD_ATTEMPTS_EXCEEDED',
  InvalidPin: 'INVALID_PIN',
  PinNotSet: 'PIN_NOT_SET',
  RefreshExpired: 'REFRESH_EXPIRED',
  PermissionDenied: 'PERMISSION_DENIED',
  NotAuthenticated: 'NOT_AUTHENTICATED',
  /**
   * `/auth/renew` specifically: the access token itself had already expired when the request
   * reached the server — a stale tab woken up, not a revocation. The one code the base query is
   * allowed to silently recover from. See `baseQuery.ts`'s `recoverSession`.
   */
  TokenExpired: 'TOKEN_EXPIRED',
  CsrfMismatch: 'CSRF_MISMATCH',

  InsufficientStock: 'INSUFFICIENT_STOCK',
  UnitNotAvailable: 'UNIT_NOT_AVAILABLE',
  PriceBelowFloor: 'PRICE_BELOW_FLOOR',
  /** A below-floor sale now raises a remote approval instead of hard-rejecting. See `approvalRequestId`. */
  PriceBelowFloorApprovalRequired: 'PRICE_BELOW_FLOOR_APPROVAL_REQUIRED',
  ShopInactive: 'SHOP_INACTIVE',
  SaleAlreadySubmitted: 'SALE_ALREADY_SUBMITTED',

  /** Not from the API. The request never left the device. */
  Offline: 'CLIENT_OFFLINE',
} as const;

export type ErrorCodeValue = (typeof ErrorCode)[keyof typeof ErrorCode];

/** What every failed query and mutation in this codebase resolves to. */
export type ApiError = {
  readonly status: number;
  readonly code: string;
  /**
   * What happened and what to do next. Never "Something went wrong" — an error a salesperson
   * cannot act on is an error that becomes a phone call to the owner.
   */
  readonly message: string;
  readonly fields?: Readonly<Record<string, readonly string[]>> | undefined;
  readonly problem?: ProblemDetails | undefined;
};

export const isApiError = (value: unknown): value is ApiError =>
  typeof value === 'object' &&
  value !== null &&
  'code' in value &&
  'message' in value &&
  typeof (value as { code: unknown }).code === 'string';

export const hasCode = (value: unknown, ...codes: readonly string[]): boolean =>
  isApiError(value) && codes.includes(value.code);

/**
 * The last-resort message. Used only when the API returned no body at all — a proxy 502, a
 * connection cut mid-response. Everything else carries its own explanation.
 */
export const FALLBACK_MESSAGE =
  'The server could not be reached. Check the connection and try again — nothing was saved.';
